<script lang="ts" setup>
import { onMounted, ref } from 'vue'

interface Props {
  src: string
  toleranceThreshold?: number
  padding?: number
  backgroundColor?: string
  [key: string]: any
}

const props = withDefaults(defineProps<Props>(), {
  toleranceThreshold: 2,
  padding: 8,
  backgroundColor: 'white'
})

const canvas = ref<HTMLCanvasElement | null>(null)
const croppedImageSrc = ref<string>('data:image/svg+xml;utf8,<svg version="1.1" width="1" height="1" xmlns="http://www.w3.org/2000/svg"></svg>')

const colorDistance = (color1: Uint8ClampedArray, color2: Uint8ClampedArray): number => {
  return Math.max(
    Math.abs(color1[0] - color2[0]),
    Math.abs(color1[1] - color2[1]),
    Math.abs(color1[2] - color2[2])
  )
}

const processImage = async () => {
  if (!canvas.value || !props.src) return

  const originalImage = new Image()
  originalImage.crossOrigin = 'anonymous'

  try {
    await new Promise((resolve, reject) => {
      originalImage.onload = resolve
      originalImage.onerror = reject
      originalImage.src = props.src
    })

    if (!canvas.value) return

    const ctx = canvas.value.getContext('2d')!
    const width = canvas.value.width = originalImage.width
    const height = canvas.value.height = originalImage.height

    ctx.fillStyle = props.backgroundColor
    ctx.fillRect(0, 0, width, height)
    const colorToTrim = ctx.getImageData(0, 0, 1, 1).data.slice(0, 3)
    ctx.drawImage(originalImage, 0, 0)

    const data = ctx.getImageData(0, 0, width, height).data
    let left = width, right = 0, top = height, bottom = 0

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4
        const distance = colorDistance(data.slice(i, i + 3), colorToTrim)
        if (distance > props.toleranceThreshold) {
          left = Math.min(left, x)
          right = Math.max(right, x)
          top = Math.min(top, y)
          bottom = Math.max(bottom, y)
        }
      }
    }

    left = Math.max(0, left - props.padding)
    right = Math.min(width - 1, right + props.padding)
    top = Math.max(0, top - props.padding)
    bottom = Math.min(height - 1, bottom + props.padding)

    const croppedWidth = right - left + 1
    const croppedHeight = bottom - top + 1

    canvas.value.width = croppedWidth
    canvas.value.height = croppedHeight
    ctx.fillStyle = props.backgroundColor
    ctx.fillRect(0, 0, croppedWidth, croppedHeight)
    ctx.drawImage(originalImage, left, top, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight)

    const blob = await new Promise<Blob>((resolve) => canvas.value!.toBlob(resolve as BlobCallback))
    croppedImageSrc.value = URL.createObjectURL(blob)
  } catch (error) {
    console.error('Error processing image:', error)
  }
}

onMounted(() => {
  if (props.src) {
    processImage()
    return () => URL.revokeObjectURL(croppedImageSrc.value)
  }
})
</script>

<template>
  <canvas ref="canvas" style="display: none;"></canvas>
  <div :style="{ '--cropped': `url('${croppedImageSrc}')` }">
    <img :src="src" v-bind="$attrs" crossorigin="anonymous" />
  </div>
</template>

<style scoped>
div {
  display: contents;
}

img {
  object-position: 2000px 2000px;
  background-image: var(--cropped);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
